import clsx from 'clsx'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import HeadData from 'components/Common/HeadData'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import styled from 'styled-components'
import ExperienceSearchPageFilters from './ExperienceSearchPageFilters'
import { replace } from 'connected-react-router'
import { deleteSearchParams } from 'lib/url/searchUrlUtils'
import GeoContext from 'contexts/geoContext'
import ExperienceSearchPageList from './ExperienceSearchPageList'
import TextLink from 'LuxKit/TextLink'
import useExperienceSearchList from 'hooks/Experiences/useExperienceSearchList'
import Places, { getPlaceByRegionCode } from 'constants/places'
import { fetchExperienceCategories } from 'actions/ExperienceActions'
import { AnythingCategory } from 'constants/experience'
import useQueryParams from 'hooks/useQueryParams'
import { useAppDispatch } from 'hooks/reduxHooks'
import { usePlaceAncestorsById, usePlaceByCoords, usePlaceById } from 'hooks/usePlace'
import { EmptyArray, without } from 'lib/array/arrayUtils'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import ExperienceSearchPageContainer from './ExperienceSearchPageContainer'
import ExperienceSearchTile from '../ExperienceTiles/ExperienceSearchTile'
import useExperience from 'hooks/Experiences/useExperience'
import Group from 'components/utils/Group'
import Error404Page from 'components/Pages/Error404Page'
import { listHasPlaceId } from './utils'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import MapWindow from 'components/Search/MapWindow'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import { getExperiencesSearchHeadMeta } from './getExperiencesSearchHeadMeta'

const PageContent = styled.div`
  display: grid;
  padding: ${rem(24)} 0;
  grid-template-columns: minmax(0, 1fr);
  grid-gap: ${rem(32)};

  ${mediaQueryUp.desktop} {
    grid-template-columns: ${rem(278)} minmax(0, 1fr);
  }
`

const DensePageContent = styled.div`
  display: grid;
  padding: ${rem(24)} 0;
  grid-template-columns: minmax(0, 1fr);

  ${mediaQueryUp.desktop} {
    grid-template-columns: ${rem(278)} minmax(0, 1fr);
    column-gap: 0;
  }
`

const DenseLayout = styled(LayoutContainer)`
  padding-left: ${rem(16)} !important;
  padding-right: ${rem(16)} !important;
`

const DesktopFilterBreakpoint = styled(CSSBreakpoint)`
  > * + * {
    margin-top: ${rem(16)};
  }
`

const DesktopDenseFilterBreakpoint = styled(CSSBreakpoint)`
  width: ${rem(250)};
  > * + * {
    margin-top: ${rem(16)};
  }
`

const FadingReset = styled(TextLink)`
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s;

  &.visible {
    pointer-events: auto;
    opacity: 1;
  }
`
interface OwnProps {
  forceCompactTiles?: boolean
}

interface MappedStateProps {
  search: string
}

type Props = OwnProps & MappedStateProps

interface ExperienceSearchPageContentProps extends Props {
  isCompactTilesEnabled: boolean
}

function ExperiencesSearchPageWithExperiment(props: Props) {
  const isCompactTilesEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.experiencesSearchCompactTiles)

  return <ExperienceSearchPageContent
    {...props}
    isCompactTilesEnabled={isCompactTilesEnabled}
  />
}

function ExperienceSearchPageContent(props: ExperienceSearchPageContentProps) {
  const { search, isCompactTilesEnabled } = props

  const intl = useIntl()
  const geo = useContext(GeoContext)
  const queryParams = useQueryParams()
  const dispatch = useAppDispatch()
  const {
    searchFilters,
  } = useExperiencesSearchFilters()
  const trackEvent = useTrackExperienceOfferEvent()

  const experienceId = queryParams.get('experienceId') ?? undefined
  const [highlightedOffer] = useExperience(experienceId)
  const [highlightedOfferPlace] = usePlaceByCoords(highlightedOffer?.location.latitude, highlightedOffer?.location.longitude)

  const placeId = (experienceId ? highlightedOfferPlace?.id : queryParams.get('destinationId')) ?? undefined

  const placeName = (experienceId ? highlightedOfferPlace?.name : queryParams.get('destinationName')) ?? undefined
  const experienceName = queryParams.get('experienceName')

  const regionPlace = useMemo(() => getPlaceByRegionCode(geo.currentRegionCode), [geo.currentRegionCode])

  const [ancestorPlaceIds, fetchingAncestors] = usePlaceAncestorsById(placeId)

  useEffect(() => {
    dispatch(fetchExperienceCategories())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fallbackPlaceIds = useMemo(() => {
    const ancestorIds = without(ancestorPlaceIds, Places.Anywhere.id)
    return [...ancestorIds, regionPlace.id, Places.Anywhere.id]
  }, [ancestorPlaceIds, regionPlace])

  const list = useExperienceSearchList({
    placeId,
    fallbackPlaceIds: fetchingAncestors ? EmptyArray : fallbackPlaceIds,
    categoryCodes: searchFilters.categoryCodes,
    priceLte: searchFilters.priceLte,
    priceGte: searchFilters.priceGte,
    luxPlusFeatures: searchFilters.luxPlusFeatures,
    collections: searchFilters.collections,
    sortBy: 'type',
  })

  const onFiltersReset = useCallback(() => {
    const nextSearch = deleteSearchParams(search, 'categories', 'priceLte', 'priceGte', 'luxPlusFeatures')
    dispatch(replace({ search: nextSearch }))
  }, [dispatch, search])

  const handleViewMapClick = useCallback(() => {
    trackEvent('VIEW_ON_MAP_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [])

  // if it's not the same id, it had to resort of a fallback id and thus, we have no results for the
  // original query
  const isDifferentPlace = list.placeId !== placeId

  const isHighlightedOfferSoldOut = highlightedOffer && highlightedOffer?.status !== 'ONLINE'
  const showHighlightedTile = highlightedOffer && !isHighlightedOfferSoldOut

  // hide the list when you only found the highlighted one
  const showList = !(list.ids.length === 1 && list.ids[0] === highlightedOffer?.id)

  const finalPlace = usePlaceById(list.placeId)
  const isAnywhereSearch = list.placeId === Places.Anywhere.id
  const metaPlaceName = !isAnywhereSearch ? (finalPlace?.name ?? placeName ?? undefined) : undefined

  const searchHeadMeta = useMemo(() => getExperiencesSearchHeadMeta({
    placeName: metaPlaceName,
    isAnywhere: isAnywhereSearch,
    intl,
  }), [metaPlaceName, isAnywhereSearch, intl])

  const canReset = useMemo(() => {
    if (searchFilters.categoryCodes?.length === 1 && searchFilters.categoryCodes[0] === AnythingCategory.id) {
      return false
    }

    return !!searchFilters.categoryCodes?.length || !!searchFilters.luxPlusFeatures?.length
  }, [searchFilters.categoryCodes, searchFilters.luxPlusFeatures])

  const searchMode = useMemo(() => ({
    url: `/search/experiences/map?${deleteSearchParams(search, 'sortBy')}`,
    mode: 'list',
  }), [search])

  if (!listHasPlaceId(list)) {
    return <Error404Page />
  }

  function Layout(props: React.ComponentProps<typeof LayoutContainer>) {
    return isCompactTilesEnabled ? <DenseLayout variant="extraLarge" {...props} /> : <LayoutContainer {...props} />
  }
  const DesktopFilterBreakpointComponent = isCompactTilesEnabled ? DesktopDenseFilterBreakpoint : DesktopFilterBreakpoint
  const PageContentComponent = isCompactTilesEnabled ? DensePageContent : PageContent

  const noResultsForExperienceHeading = intl.formatMessage(
    {
      id: 'pgTMxcXmp5',
      defaultMessage: "Sorry, we couldn't find any experiences for {name}",
      description: 'Heading when a highlighted experience offer is sold out',
    },
    { name: experienceName },
  )
  const noResultsNearPlaceHeading = intl.formatMessage(
    {
      id: '7wJoEbhHXW',
      defaultMessage: "Sorry, we couldn't find any experiences near {placeName}",
      description: 'Heading when no experiences are found near a searched place',
    },
    { placeName },
  )
  const tryDifferentLocationText = list.placeId !== Places.Anywhere.id
    ? intl.formatMessage(
        {
          id: 'z/5zerbj08',
          defaultMessage: 'Try a different location or explore our top offers in {placeName}',
          description: 'Suggestion text with place name when no experience results found',
        },
        { placeName: finalPlace?.name },
      )
    : intl.formatMessage({
        id: 'JrkUbExIe2',
        defaultMessage: 'Try a different location or explore our top offers',
        description: 'Suggestion text when no experience results found',
      })

  return <ExperienceSearchPageContainer
    searchMode={searchMode}
    list={list}
    place={finalPlace}
  >
    <HeadData
      noIndex
      title={searchHeadMeta.title}
      description={searchHeadMeta.description}
      imageId={searchHeadMeta.imageId}
      imageUrl={searchHeadMeta.imageUrl}
      imageAlt={searchHeadMeta.imageAlt}
    />
    <Pane background="neutral-six">
      <Layout>
        <PageContentComponent>
          <DesktopFilterBreakpointComponent min="desktop">
            <VerticalSpacer gap={24}>
              <MapWindow mapViewURL={searchMode.url} onClick={handleViewMapClick} />
              <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
                <Heading variant="heading3">{intl.formatMessage({
                  id: 'wqw2JKfveM',
                  defaultMessage: 'Filter by',
                  description: 'Heading for the desktop experience search filters sidebar',
                })}
                </Heading>
                <FadingReset
                  className={clsx({ visible: canReset })}
                  size="small"
                  onClick={onFiltersReset}
                >
                  {intl.formatMessage({
                    id: 'fPWgVH7zqo',
                    defaultMessage: 'Reset all',
                    description: 'Button to reset all experience search filters',
                  })}
                </FadingReset>
              </Group>
              <ExperienceSearchPageFilters
                place={finalPlace}
              />
            </VerticalSpacer>
          </DesktopFilterBreakpointComponent>
          <VerticalSpacer gap={40}>
            {showHighlightedTile && <ExperienceSearchTile
              experience={highlightedOffer}
            />}

            {(isHighlightedOfferSoldOut || isDifferentPlace) && <div>
              <Heading variant="heading4">{isHighlightedOfferSoldOut ? noResultsForExperienceHeading : noResultsNearPlaceHeading}</Heading>
              <BodyText variant="large">
                {tryDifferentLocationText}
              </BodyText>
            </div>}

            {showList && <ExperienceSearchPageList
              list={list}
              destinationName={finalPlace?.name!}
              excludeIds={highlightedOffer ? [highlightedOffer.id] : undefined}
              isCompactTilesEnabled={isCompactTilesEnabled}
            />}
          </VerticalSpacer>
        </PageContentComponent>
      </Layout>
    </Pane>
  </ExperienceSearchPageContainer>
}

function ExperiencesSearchPage(props: Props) {
  const { forceCompactTiles } = props

  if (forceCompactTiles) {
    return <ExperienceSearchPageContent
      {...props}
      isCompactTilesEnabled
    />
  }

  return <ExperiencesSearchPageWithExperiment {...props} />
}

function mapStateToProps(state: App.State, _ownProps: OwnProps): MappedStateProps {
  return {
    search: state.router.location.search,
  }
}

export default connect<MappedStateProps, undefined, OwnProps, App.State>(mapStateToProps)(ExperiencesSearchPage)
